/**
 * ChecklistSync - Manages bidirectional sync between script and heading checklists
 *
 * When a script checkbox is toggled, all headings containing that script are updated.
 * When heading checkboxes change, script states are recomputed.
 */

import type { ScriptHeadingMapping } from './ScriptHeadingMapper.js';

/**
 * Script checkbox state
 * - unchecked: no headings for this script are checked
 * - partial: some (but not all) headings for this script are checked
 * - checked: all headings for this script are checked
 */
export type ScriptCheckState = 'unchecked' | 'partial' | 'checked';

/**
 * Configuration for ChecklistSync
 */
export interface ChecklistSyncConfig {
  /** Get the current list of checked heading slugs */
  getCheckedSlugs: () => string[];
  /** Set the list of checked heading slugs */
  setCheckedSlugs: (slugs: string[]) => void;
  /** Called when script states change (for UI update) */
  onScriptStateChange: () => void;
}

/**
 * Manages bidirectional synchronization between script completion
 * and heading checklists.
 */
export class ChecklistSync {
  private mapping: ScriptHeadingMapping | null = null;
  private config: ChecklistSyncConfig;

  constructor(config: ChecklistSyncConfig) {
    this.config = config;
  }

  /**
   * Set the script-heading mapping
   * Call this after weave/tangle to update the mapping
   */
  setMapping(mapping: ScriptHeadingMapping): void {
    this.mapping = mapping;
  }

  /**
   * Clear the mapping (e.g., when switching files)
   */
  clearMapping(): void {
    this.mapping = null;
  }

  /**
   * Check if mapping is available
   */
  hasMapping(): boolean {
    return this.mapping !== null;
  }

  /**
   * Get all script names from the mapping
   */
  getScriptNames(): string[] {
    if (!this.mapping) return [];
    return Array.from(this.mapping.scriptToHeadings.keys());
  }

  /**
   * Get the check state for a specific script
   */
  getScriptCheckState(scriptName: string): ScriptCheckState {
    if (!this.mapping) return 'unchecked';

    const headings = this.mapping.scriptToHeadings.get(scriptName);
    if (!headings || headings.length === 0) return 'unchecked';

    const checkedSlugs = new Set(this.config.getCheckedSlugs());
    const checkedCount = headings.filter(h => checkedSlugs.has(h)).length;

    if (checkedCount === 0) return 'unchecked';
    if (checkedCount === headings.length) return 'checked';
    return 'partial';
  }

  /**
   * Get check states for all scripts
   */
  getAllScriptStates(): Map<string, ScriptCheckState> {
    const states = new Map<string, ScriptCheckState>();
    if (!this.mapping) return states;

    for (const scriptName of this.mapping.scriptToHeadings.keys()) {
      states.set(scriptName, this.getScriptCheckState(scriptName));
    }
    return states;
  }

  /**
   * Called when a script checkbox is toggled
   * Updates all headings associated with that script
   */
  onScriptToggle(scriptName: string, checked: boolean): void {
    if (!this.mapping) return;

    const headings = this.mapping.scriptToHeadings.get(scriptName);
    if (!headings || headings.length === 0) return;

    const currentSlugs = new Set(this.config.getCheckedSlugs());

    if (checked) {
      // Check all headings for this script
      for (const slug of headings) {
        currentSlugs.add(slug);
      }
    } else {
      // Uncheck headings, but only if they don't have other checked scripts
      for (const slug of headings) {
        const scriptsForHeading = this.mapping.headingToScripts.get(slug) || [];
        // Check if any OTHER script (not this one) that references this heading is fully checked
        const hasOtherCheckedScript = scriptsForHeading.some(s =>
          s !== scriptName && this.isScriptFullyChecked(s, currentSlugs)
        );
        if (!hasOtherCheckedScript) {
          currentSlugs.delete(slug);
        }
      }
    }

    this.config.setCheckedSlugs(Array.from(currentSlugs));
    this.config.onScriptStateChange();
  }

  /**
   * Called when a heading checkbox is toggled
   * Just triggers a UI update since the slug is already updated externally
   */
  onHeadingToggle(): void {
    this.config.onScriptStateChange();
  }

  /**
   * Check if a script is fully checked (all its headings are checked)
   * Uses provided slugSet to check state before updating it
   */
  private isScriptFullyChecked(scriptName: string, checkedSlugs: Set<string>): boolean {
    if (!this.mapping) return false;

    const headings = this.mapping.scriptToHeadings.get(scriptName);
    if (!headings || headings.length === 0) return false;

    return headings.every(h => checkedSlugs.has(h));
  }
}
